"use client"

import { useState } from "react"
import { Check, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Checkbox } from "@/components/ui/checkbox"
import type { FieldConfiguration } from "@/types/workflow"
import { useTranslation } from "@/hooks/use-translation"

interface MultiSelectCellProps {
  value: string[]
  options: { value: string; label: string; color?: string }[]
  column: FieldConfiguration
  rowId: string
  onSave: (value: string[]) => void
  onCancel: () => void
}

export function MultiSelectCell({
  value,
  options,
  column,
  rowId,
  onSave,
  onCancel,
}: MultiSelectCellProps) {
  const [selected, setSelected] = useState<string[]>(value || [])
  const { t } = useTranslation()

  const toggleOption = (val: string) => {
    setSelected(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    )
  }

  return (
    <Dialog open onOpenChange={open => { if (!open) onCancel() }}>
      <DialogContent className="max-w-sm w-full p-6 flex flex-col gap-4">
        <DialogTitle>{t('selectOptions')}</DialogTitle>
        <div className="flex flex-col gap-2 max-h-[50vh] overflow-auto">
          {options.map(option => (
            <label key={option.value} className="flex items-center gap-2 cursor-pointer px-1 py-1 rounded hover:bg-accent">
              <Checkbox
                checked={selected.includes(option.value)}
                onCheckedChange={() => toggleOption(option.value)}
                id={`option-${option.value}`}
              />
              {option.color && (
                <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: option.color }} />
              )}
              <span>{option.label}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-2 justify-end mt-2">
          <Button size="sm" variant="ghost" onClick={() => onSave(selected)} aria-label={t('apply')} className="flex items-center gap-1">
            <Check className="w-5 h-5 text-green-600" />
            <span>{t('apply')}</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={onCancel} aria-label={t('cancel')} className="flex items-center gap-1">
            <X className="w-5 h-5 text-red-500" />
            <span>{t('cancel')}</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
} 